import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import AdultGate from '@/components/AdultGate';
import { Lock, LockOpen, ShieldCheck, X, Plus, Eye, Heart, Sparkle } from '@phosphor-icons/react';

const fileToB64 = (file) => new Promise((res, rej) => {
  const r = new FileReader();
  r.onload = () => res(String(r.result || '').split(',')[1] || '');
  r.onerror = rej; r.readAsDataURL(file);
});
const fmtAgo = (iso) => {
  const m = (Date.now() - new Date(iso).getTime()) / 60000;
  if (m < 60) return `${Math.floor(m)}m`;
  if (m < 1440) return `${Math.floor(m / 60)}h`;
  return `${Math.floor(m / 1440)}d`;
};

const InviteModal = ({ open, onClose, onSent }) => {
  const [bubbles, setBubbles] = useState([]);
  const [fromId, setFromId] = useState('');
  const [toId, setToId] = useState('');
  const [kind, setKind] = useState('photo');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    api.get('/bubbles/mine').then(({ data }) => {
      setBubbles(data.bubbles || []);
      if (data.bubbles?.[0]) setFromId(data.bubbles[0].id);
    });
  }, [open]);
  const submit = async () => {
    if (!fromId || !toId.trim()) return;
    setBusy(true);
    try {
      await api.post('/intimate/invite', {
        from_bubble_id: fromId, to_bubble_id: toId.trim(),
        kind, note: note.trim() || null,
      });
      toast.success('invitation sent.');
      onSent?.(); onClose?.();
      setNote(''); setToId('');
    } catch (err) { toast.error(err?.response?.data?.detail || 'could not'); }
    finally { setBusy(false); }
  };
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[120] bg-black/85 backdrop-blur-xl grid place-items-center px-4" onClick={onClose}>
      <div className="w-full max-w-md bg-neutral-950 rounded-3xl p-6 relative ring-1 ring-rose-500/30" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-3 right-3 p-2 rounded-full hover:bg-white/10 text-white/60"><X size={16} weight="bold" /></button>
        <div className="text-center mb-5">
          <Lock size={28} weight="duotone" className="text-rose-400 mx-auto" />
          <div className="font-display font-bold text-xl mt-2">Invite to an exchange</div>
          <div className="text-xs text-white/55 mt-1">Both bubbles unlock at the same time. Nothing before consent.</div>
        </div>
        <div className="text-[10px] uppercase tracking-widest text-white/60 mb-1">from</div>
        <select value={fromId} onChange={(e) => setFromId(e.target.value)}
          data-testid="int-from"
          className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm focus:outline-none mb-3">
          {bubbles.map((b) => (<option key={b.id} value={b.id}>{b.name}</option>))}
        </select>
        <div className="text-[10px] uppercase tracking-widest text-white/60 mb-1">to · bubble id</div>
        <input value={toId} onChange={(e) => setToId(e.target.value)}
          data-testid="int-to"
          placeholder="paste the bubble's id"
          className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm focus:outline-none mb-3" />
        <div className="grid grid-cols-2 gap-2 mb-3">
          {[
            { id: 'photo', label: 'Photo for photo' },
            { id: 'clip', label: 'Clip for clip' },
          ].map((k) => (
            <button key={k.id} data-testid={`int-kind-${k.id}`} onClick={() => setKind(k.id)}
              className={`rounded-xl py-2 text-xs font-bold border ${
                kind === k.id ? 'border-rose-500 bg-rose-500/10 text-rose-300' : 'border-white/10 text-white/60'
              }`}>{k.label}</button>
          ))}
        </div>
        <textarea value={note} onChange={(e) => setNote(e.target.value.slice(0, 120))}
          data-testid="int-note"
          placeholder="one line (optional)" rows={2}
          className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm focus:outline-none mb-4" />
        <button data-testid="int-send" onClick={submit} disabled={busy || !toId.trim()}
          className="w-full bg-rose-500 hover:bg-rose-400 text-white py-3 rounded-full font-bold text-sm disabled:opacity-30">
          {busy ? 'sending…' : 'Send'}
        </button>
      </div>
    </div>
  );
};

const UploadPanel = ({ exch, onUploaded }) => {
  const isPhoto = exch.kind === 'photo';
  const [b64, setB64] = useState(null);
  const [busy, setBusy] = useState(false);
  const onFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 8_000_000) return toast.error('max 8mb');
    setB64(await fileToB64(f));
  };
  const upload = async () => {
    if (!b64) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/intimate/${exch.id}/upload`, { media_b64: b64 });
      toast.success(data.status === 'revealed' ? 'revealed.' : 'waiting on them…');
      onUploaded?.();
    } catch (err) { toast.error(err?.response?.data?.detail || 'could not'); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-3 rounded-xl border border-white/10 p-3">
      <input type="file"
        data-testid={`int-upload-${exch.id}`}
        accept={isPhoto ? 'image/*' : 'video/*'} onChange={onFile}
        className="text-xs text-white/60 mb-2" />
      {b64 && (isPhoto ? (
        <img alt="" src={`data:image/png;base64,${b64}`} className="rounded-lg max-h-32 mb-2" />
      ) : (
        <video controls src={`data:video/webm;base64,${b64}`} className="rounded-lg max-h-32 mb-2" />
      ))}
      <button data-testid={`int-send-blob-${exch.id}`} onClick={upload} disabled={busy || !b64}
        className="w-full bg-rose-500 hover:bg-rose-400 text-white py-2 rounded-full text-xs font-bold disabled:opacity-30">
        {busy ? 'sending…' : 'Lock it in'}
      </button>
    </div>
  );
};

const Vault = () => {
  const [exchanges, setExchanges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [gated, setGated] = useState(false);
  const [openInvite, setOpenInvite] = useState(false);
  const [adultOpen, setAdultOpen] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get('/intimate/mine');
      setExchanges(data.exchanges || []);
      setGated(false);
    } catch (err) {
      if (err?.response?.status === 403) setGated(true);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const accept = async (id) => {
    try { await api.post(`/intimate/${id}/accept`, { accept: true }); toast.success('accepted.'); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'could not'); }
  };
  const decline = async (id) => {
    try { await api.post(`/intimate/${id}/accept`, { accept: false }); load(); }
    catch { /* silent */ }
  };

  if (loading) return <div className="min-h-screen grid place-items-center text-white/60">listening…</div>;

  if (gated) {
    return (
      <div className="max-w-xl mx-auto py-20 px-4 text-center">
        <ShieldCheck size={40} weight="duotone" className="text-rose-400 mx-auto" />
        <h1 className="font-display text-4xl font-black mt-4">The Vault</h1>
        <p className="text-white/60 mt-2 text-sm">21+ only. Private exchanges between bubbles. Consent-locked.</p>
        <button onClick={() => setAdultOpen(true)}
          data-testid="vault-gate-verify"
          className="mt-6 px-6 py-3 rounded-full bg-rose-500 hover:bg-rose-400 text-white text-sm font-bold">
          Verify your age
        </button>
        <AdultGate open={adultOpen} onClose={() => setAdultOpen(false)} onVerified={load} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-neutral-950 via-rose-950/30 to-black">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10 pb-24">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-8">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-rose-300">21+ · consent-locked</div>
            <h1 className="font-display text-4xl sm:text-5xl font-black mt-1 bg-gradient-to-r from-rose-300 to-amber-200 bg-clip-text text-transparent">The Vault</h1>
            <p className="text-white/60 mt-2 text-sm max-w-xl">
              Invite another bubble. Both upload a photo or 12s clip. Both reveal at the same time. Never before.
            </p>
          </div>
          <button data-testid="open-int-invite" onClick={() => setOpenInvite(true)}
            className="bg-rose-500 hover:bg-rose-400 text-white px-5 py-3 rounded-full text-sm font-bold flex items-center gap-2">
            <Plus size={14} weight="bold" /> Invite
          </button>
        </div>

        {exchanges.length === 0 ? (
          <div className="rounded-3xl bg-black/40 ring-1 ring-rose-500/20 p-10 text-center">
            <Lock size={28} weight="duotone" className="text-rose-300 mx-auto mb-2" />
            <div className="text-white/60">Empty vault. Invite someone.</div>
          </div>
        ) : (
          <div className="space-y-3">
            {exchanges.map((e) => {
              const me_from = e.from_user_id;
              const my = !!localStorage.getItem('bx_user') && JSON.parse(localStorage.getItem('bx_user')).id === me_from;
              const myMedia = my ? e.media_from_url : e.media_to_url;
              const theirMedia = my ? e.media_to_url : e.media_from_url;
              const revealed = e.status === 'revealed';
              return (
                <div key={e.id} data-testid={`vault-${e.id}`}
                  className="rounded-2xl bg-black/40 ring-1 ring-rose-500/20 p-5">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-rose-300">
                        {my ? 'you invited' : 'invited you'} · {e.kind}
                      </div>
                      <div className="text-sm font-bold">
                        {my ? `${e.from_bubble_name} → ${e.to_bubble_name}` : `${e.from_bubble_name} → you`}
                      </div>
                    </div>
                    <div className={`flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold ${
                      revealed ? 'text-amber-300' : e.status === 'declined' ? 'text-white/55' : 'text-rose-300'
                    }`}>
                      {revealed ? <LockOpen size={12} weight="fill" /> : <Lock size={12} weight="fill" />}
                      {e.status}
                    </div>
                  </div>
                  {e.note && <div className="text-sm italic text-white/70 mb-2">"{e.note}"</div>}
                  {!my && e.status === 'invited' && (
                    <div className="flex gap-2 mt-2">
                      <button data-testid={`int-accept-${e.id}`} onClick={() => accept(e.id)}
                        className="flex-1 bg-rose-500 hover:bg-rose-400 text-white py-2 rounded-full text-xs font-bold">Accept</button>
                      <button data-testid={`int-decline-${e.id}`} onClick={() => decline(e.id)}
                        className="px-4 py-2 rounded-full bg-white/5 hover:bg-white/10 text-xs text-white/60">Decline</button>
                    </div>
                  )}
                  {e.status === 'accepted' && (
                    <>
                      <div className="text-[10px] text-white/60 mt-2">
                        {myMedia ? '✓ you uploaded · waiting on them' : 'upload yours — they\'ve agreed'}
                      </div>
                      {!myMedia && <UploadPanel exch={e} onUploaded={load} />}
                    </>
                  )}
                  {revealed && (
                    <div className="mt-3 grid grid-cols-2 gap-2">
                      <div>
                        <div className="text-[10px] uppercase tracking-widest text-white/60 mb-1">yours</div>
                        {e.kind === 'photo' ? (
                          <img alt="" src={`${process.env.REACT_APP_BACKEND_URL}${myMedia}`}
                            className="w-full rounded-lg" />
                        ) : (
                          <video controls src={`${process.env.REACT_APP_BACKEND_URL}${myMedia}`}
                            className="w-full rounded-lg" />
                        )}
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-widest text-amber-300 mb-1">theirs</div>
                        {e.kind === 'photo' ? (
                          <img alt="" src={`${process.env.REACT_APP_BACKEND_URL}${theirMedia}`}
                            className="w-full rounded-lg" />
                        ) : (
                          <video controls src={`${process.env.REACT_APP_BACKEND_URL}${theirMedia}`}
                            className="w-full rounded-lg" />
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <InviteModal open={openInvite} onClose={() => setOpenInvite(false)} onSent={load} />
      </div>
    </div>
  );
};

export default Vault;
